import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaHome, FaArrowLeft } from 'react-icons/fa';
import { RiScissorsLine } from 'react-icons/ri';

const NotFound = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    // Navigate to gallery with search query
    navigate(`/studio-hair-salon?search=${encodeURIComponent(searchQuery)}`);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  // Scissors animation
  const scissorsVariants = {
    animate: {
      rotate: [0, 45, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-2xl w-full space-y-8 text-center"
      >
        {/* 404 Header with Animated Scissors */}
        <motion.div variants={itemVariants} className="relative">
          <h1 className="text-9xl font-bold text-gray-200">404</h1>
          <motion.div
            variants={scissorsVariants}
            animate="animate"
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          >
            <RiScissorsLine className="text-8xl text-teal-500" />
          </motion.div>
        </motion.div>

        {/* Message */}
        <motion.div variants={itemVariants} className="space-y-4">
          <h2 className="text-3xl font-bold text-gray-900">
            Oops! This page has been styled away
          </h2>
          <p className="text-gray-600 text-lg">
            The hairstyle you're looking for might have been moved, renamed, or is temporarily unavailable.
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.form 
          variants={itemVariants}
          onSubmit={handleSearch}
          className="max-w-md mx-auto"
        >
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for hairstyles..."
              className="w-full px-4 py-3 pl-12 pr-16 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent shadow-sm"
            />
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <button
              type="submit"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors duration-200"
            >
              Search
            </button>
          </div>
        </motion.form>

        {/* Navigation Links */}
        <motion.div variants={itemVariants} className="flex justify-center space-x-4">
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 text-teal-600 hover:text-teal-800 transition-colors duration-200"
          >
            <FaHome className="mr-2" /> Go Home
          </Link>
          <Link
            to="/studio-hair-salon"
            className="inline-flex items-center px-4 py-2 text-teal-600 hover:text-teal-800 transition-colors duration-200"
          >
            <FaArrowLeft className="mr-2" /> Back to Gallery
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default NotFound;
